import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Table, Search, Calendar, ArrowUpDown, ListTodo, NotebookTabs, Microscope, Images, Bot, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatOperationType, getOperationTypeIcon, getOperationTypeColor } from '@/utils/operation-type-formatter';
import { useUsageLogs } from '@/hooks/usage/useUsageData';
import { format } from 'date-fns';

interface UsageDetailsTableProps {
  timeRange: '7d' | '30d' | '90d' | 'all';
}

interface UsageLogDisplay {
  id: string;
  date: string;
  app: string;
  function: string;
  service: 'OpenAI' | 'Perplexity' | 'Runware';
  operation: string;
  cost: number;
}

const UsageDetailsTable: React.FC<UsageDetailsTableProps> = ({ timeRange }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof UsageLogDisplay>('date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  const { data: logs, isLoading, error } = useUsageLogs(timeRange, 50, 0);

  // Transform real data to display format
  const displayLogs: UsageLogDisplay[] = (logs || []).map(log => ({
    id: log.id,
    date: format(new Date(log.created_at), 'yyyy-MM-dd HH:mm'),
    app: log.app_name || 'Unknown App',
    function: log.function_name || 'Unknown Function',
    service: log.service_provider as 'OpenAI' | 'Perplexity' | 'Runware',
    operation: log.operation_type,
    cost: log.credits || 0
  }));

  const getServiceColor = (service: string) => {
    switch (service) {
      case 'OpenAI': return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300';
      case 'Perplexity': return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300';
      case 'Runware': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300';
    }
  };

  const getAppIcon = (appName: string) => {
    switch (appName) {
      case 'Task Manager': return <ListTodo className="w-4 h-4 text-blue-500" />;
      case 'Workbook': return <NotebookTabs className="w-4 h-4 text-purple-500" />;
      case 'Research Generator': return <Microscope className="w-4 h-4 text-emerald-500" />;
      case 'Image Generator': return <Images className="w-4 h-4 text-red-500" />;
      case 'AI Agents': return <Bot className="w-4 h-4 text-amber-500" />;
      default: return <Table className="w-4 h-4 text-gray-500" />;
    }
  };

  const getFunctionIcon = (functionName: string) => {
    switch (functionName) {
      case 'Chat Conversation': return '🤖';
      case 'Page Generation': return '📄';
      case 'Recommended Research': return '🔍';
      case 'Recommended Tasks': return '📋';
      case 'Image Generation': return '🎨';
      default: return '⚡';
    }
  };

  const handleSort = (field: keyof UsageLogDisplay) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Helper function to map app names for display purposes
  const getDisplayAppName = (appName: string): string => {
    switch (appName) {
      case 'Content Generator':
        return 'Workbook';
      case 'Image Generator':
        return 'Image Generator';
      case 'Research Generator':
        return 'Research Generator';
      case 'Research Library':
        return 'Research Generator';
      default:
        return appName;
    }
  };

  if (isLoading) {
    return (
      <div className="relative rounded-2xl bg-gradient-to-br from-white/80 via-white/90 to-white/80 
                     dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 
                     backdrop-blur-xl border border-white/20 dark:border-gray-700/30 
                     shadow-xl p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600 dark:text-gray-400">Loading usage data...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="relative rounded-2xl bg-gradient-to-br from-white/80 via-white/90 to-white/80 
                     dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 
                     backdrop-blur-xl border border-white/20 dark:border-gray-700/30 
                     shadow-xl p-6 text-center">
        <div className="text-red-500">
          Error loading usage data: {error.message}
        </div>
      </div>
    );
  }

  const filteredAndSortedLogs = displayLogs
    .filter(log => 
      log.app.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.function.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.service.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      const aValue = a[sortField];
      const bValue = b[sortField];
      const direction = sortDirection === 'asc' ? 1 : -1;
      
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return aValue.localeCompare(bValue) * direction;
      }
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return (aValue - bValue) * direction;
      }
      return 0;
    });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.2 }}
      className="relative rounded-2xl bg-gradient-to-br from-white/80 via-white/90 to-white/80 
                 dark:from-gray-900/80 dark:via-gray-900/90 dark:to-gray-900/80 
                 backdrop-blur-xl border border-white/20 dark:border-gray-700/30 
                 shadow-xl hover:shadow-2xl transition-all duration-300"
    >
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-500/5 via-transparent to-gray-500/5 
                      dark:from-slate-500/10 dark:to-gray-500/10 rounded-2xl" />
      
      <div className="relative p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-slate-500 to-gray-600 
                           flex items-center justify-center shadow-lg">
              <Table className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                Detailed Usage Logs
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Complete history of AI requests
              </p>
            </div>
          </div>
          
          {/* Search */}
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 z-20">
              <Search size={16} className="text-accent" />
            </div>
            <Input
              type="text"
              placeholder="Search logs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 text-sm font-medium w-64 bg-white/30 dark:bg-white/10 backdrop-blur-sm border border-accent/20 focus:border-accent/40 hover:border-accent/30"
            />
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200/50 dark:border-gray-700/50">
                {[
                  { key: 'date', label: 'Date & Time' },
                  { key: 'app', label: 'App' },
                  { key: 'function', label: 'Function' },
                  { key: 'cost', label: 'Cost' }
                ].map((column) => (
                  <th
                    key={column.key}
                    className="text-left py-3 px-4 font-medium text-gray-600 dark:text-gray-300 cursor-pointer 
                              hover:text-gray-900 dark:hover:text-white transition-colors"
                    onClick={() => handleSort(column.key as keyof UsageLogDisplay)}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{column.label}</span>
                      <ArrowUpDown className="w-3 h-3" />
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedLogs.map((log, index) => (
                <motion.tr
                  key={log.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2, delay: index * 0.05 }}
                  className="border-b border-gray-100/50 dark:border-gray-800/50 
                            hover:bg-white/30 dark:hover:bg-gray-800/30 transition-colors"
                >
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                    {log.date}
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                    <div className="flex items-center gap-2">
                      {getAppIcon(log.app)}
                      <span>{getDisplayAppName(log.app)}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-300">
                    <div className="flex items-center gap-2">
                      <span className="text-base">{getFunctionIcon(log.function)}</span>
                      <span>{log.function}</span>
                    </div>
                  </td>
                  <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-white">
                    {log.cost} Credits
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200/50 dark:border-gray-700/50">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Showing {filteredAndSortedLogs.length} of {displayLogs.length} entries
          </p>
          <div className="flex items-center space-x-2">
            <Button variant="outline" size="sm" disabled>
              Previous
            </Button>
            <Button variant="outline" size="sm" disabled>
              Next
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default UsageDetailsTable; 